/**
 * V1.5.8.45 (Feb 19, 2026) — Superadmin → Users search + relink page.
 *
 * User's parked P1: "Superadmin → Users → Search by email page. Must
 * load the top 100 recent users on mount, or allow searching via email.
 * View needs to aggregate User account status, Candidate rows + link
 * statuses, Resume Studio sessions + tiers, and a one-click 'Relink
 * orphans' button."
 *
 * Backed by:
 *   GET  /api/superadmin/users/search?q=&limit=&role=
 *   POST /api/superadmin/users/{uid}/relink-orphans
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    MagnifyingGlass, ShieldCheck, User, Envelope, Phone, Warning,
    CheckCircle, Link, Bandaids, ArrowsClockwise, UserSwitch,
} from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";


const ROLE_COLOR = {
    candidate: "bg-cobalt/10 text-cobalt",
    recruiter: "bg-[#7E22CE]/10 text-[#7E22CE]",
    admin: "bg-[#F59E0B]/10 text-[#F59E0B]",
    hiring_manager: "bg-[#059669]/10 text-[#059669]",
    superadmin: "bg-red-100 text-red-700",
};


export default function SuperAdminUsers() {
    const [q, setQ] = useState("");
    const [roleFilter, setRoleFilter] = useState("");
    const [busy, setBusy] = useState(false);
    const [data, setData] = useState({ count: 0, users: [] });
    const { setUser } = useAuth();
    const nav = useNavigate();

    const load = useCallback(async () => {
        setBusy(true);
        try {
            const params = new URLSearchParams();
            if (q.trim()) params.set("q", q.trim());
            // Show only top 10 most-recent when browsing without a query,
            // 100 when the user is actively searching.
            params.set("limit", q.trim() ? "100" : "10");
            if (roleFilter) params.set("role", roleFilter);
            const r = await api.get(`/superadmin/users/search?${params}`);
            setData(r.data);
        } catch (e) {
            toast.error(formatApiError(e) || "Search failed.");
        } finally { setBusy(false); }
    }, [q, roleFilter]);

    // Reload whenever the role filter changes so "top 10 recent of type" works
    // without needing to hit Search.
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [roleFilter]);

    const onSubmit = (e) => { e.preventDefault(); load(); };

    // ------- Impersonate (Login as) -------
    // Same two-step confirm the workspace-detail page uses. Superadmin acts
    // as the target user for 30 min; exit banner reverses it.
    const impersonate = useCallback(async (u) => {
        const doImpersonate = async () => {
            try {
                const r = await api.post("/superadmin/impersonate", {
                    user_id: u.id,
                    reason: "Superadmin Users search — Login as",
                });
                const p = r.data.impersonating;
                const detail = { email: p.email, role: p.role, workspace_id: p.workspace_id };
                localStorage.setItem("naukri:impersonation", JSON.stringify(detail));
                window.dispatchEvent(new CustomEvent("naukri:impersonation", { detail }));
                const me = await api.get("/auth/me");
                setUser(me.data);
                toast.success(`Now impersonating ${p.email}`);
                nav(p.role === "candidate" ? "/candidate" : "/dashboard");
            } catch (err) {
                toast.error(formatApiError(err) || "Couldn't start impersonation");
            }
        };
        toast(`Login as ${u.email || u.name || u.id}?`, {
            description: "You'll act as this user for 30 minutes. Exit any time from the top banner.",
            action: { label: "Login as", onClick: doImpersonate },
            cancel: { label: "Cancel", onClick: () => {} },
            duration: 10000,
        });
    }, [setUser, nav]);

    return (
        <div
            data-testid="superadmin-users-page"
            className="px-3 sm:px-8 py-5 sm:py-10 max-w-[1200px] mx-auto overflow-x-hidden"
        >
            <div className="mb-6">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2 inline-flex items-center gap-1.5">
                    <ShieldCheck size={12} weight="bold" /> Superadmin · Users
                </p>
                <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-ink">
                    Users & orphan relink.
                </h1>
                <p className="text-sm text-[#525252] mt-2 max-w-2xl">
                    Paste a user id, or search by email / name / phone. Pick a
                    role to browse the 10 most-recent users of that type.
                    "Login as" impersonates the user for 30 min (fully
                    reversible via the top banner).
                </p>
            </div>

            <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-2 mb-4">
                <div className="flex-1 relative">
                    <MagnifyingGlass size={16} weight="bold"
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" />
                    <input
                        type="text"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Paste user id, or search email / name / phone…"
                        data-testid="users-search-input"
                        className="w-full border border-[#E5E5E5] pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-ink"
                    />
                </div>
                <select
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                    data-testid="users-role-filter"
                    className="border border-[#E5E5E5] px-3 py-2.5 text-sm focus:outline-none focus:border-ink bg-white"
                >
                    <option value="">All roles</option>
                    <option value="candidate">Candidate</option>
                    <option value="recruiter">Recruiter</option>
                    <option value="admin">Admin</option>
                    <option value="hiring_manager">Hiring Manager</option>
                    <option value="superadmin">Superadmin</option>
                </select>
                <button
                    type="submit"
                    disabled={busy}
                    data-testid="users-search-btn"
                    className="inline-flex items-center gap-2 px-5 py-2.5 text-xs font-black uppercase tracking-wider bg-ink text-white hover:opacity-90 disabled:opacity-40"
                >
                    {busy ? <ArrowsClockwise size={14} weight="bold" className="animate-spin" /> : <MagnifyingGlass size={14} weight="bold" />}
                    {busy ? "Loading…" : "Search"}
                </button>
            </form>

            <p className="text-xs text-[#525252] mb-3" data-testid="users-search-count">
                {data.count} user{data.count === 1 ? "" : "s"}
                {data.query ? ` for "${data.query}"` : (roleFilter ? ` (top 10 recent ${roleFilter.replace("_", " ")}s)` : " (top 10 recent)")}
            </p>

            <div className="space-y-3">
                {data.users.map((u) => (
                    <UserCard key={u.id} u={u} onRelinked={load} onImpersonate={impersonate} />
                ))}
                {data.count === 0 && !busy && (
                    <div className="border border-dashed border-[#E5E5E5] p-8 text-center text-sm text-[#A3A3A3]">
                        No users match your query.
                    </div>
                )}
            </div>
        </div>
    );
}


function UserCard({ u, onRelinked, onImpersonate }) {
    const [expanded, setExpanded] = useState(false);
    const [busy, setBusy] = useState(false);
    const roleCls = ROLE_COLOR[u.role] || "bg-[#F5F5F5] text-[#525252]";
    const hasOrphans = (u.email_orphans || []).length > 0;
    const hasProblems = (u.problems || []).length > 0;

    const relink = async () => {
        if (!window.confirm(
            `Link ${u.email_orphans.length} candidate row(s) matching "${u.email}" to this user? ` +
            `Fill-blanks-only — existing candidate_user_id values are never overwritten.`
        )) return;
        setBusy(true);
        try {
            const r = await api.post(`/superadmin/users/${u.id}/relink-orphans`);
            toast.success(`Linked ${r.data.linked} candidate row(s) to ${u.email}`);
            onRelinked && onRelinked();
        } catch (e) {
            toast.error(formatApiError(e) || "Relink failed.");
        } finally { setBusy(false); }
    };

    return (
        <div
            data-testid={`user-card-${u.id}`}
            className="border border-[#E5E5E5] bg-white p-4"
        >
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0 sm:min-w-[240px]">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                        <p className="font-heading text-lg font-black tracking-tight text-ink">
                            {u.name || u.email || u.id}
                        </p>
                        <span className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 ${roleCls}`}>
                            {u.role || "unknown"}
                        </span>
                        {hasProblems && (
                            <span className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 bg-[#FEF3C7] text-[#92400E] inline-flex items-center gap-1">
                                <Warning size={10} weight="bold" />
                                {u.problems.length} issue{u.problems.length === 1 ? "" : "s"}
                            </span>
                        )}
                        {!hasProblems && (
                            <span className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 bg-emerald-50 text-emerald-700 inline-flex items-center gap-1">
                                <CheckCircle size={10} weight="bold" />
                                clean
                            </span>
                        )}
                    </div>
                    <p className="text-xs text-[#525252] flex flex-wrap gap-x-3 gap-y-0.5">
                        {u.email && <span className="inline-flex items-center gap-1"><Envelope size={10} weight="bold" /> {u.email}</span>}
                        {u.phone && <span className="inline-flex items-center gap-1"><Phone size={10} weight="bold" /> {u.phone}</span>}
                        {u.workspace_id && <span className="text-[10px] font-mono text-[#A3A3A3]">ws:{u.workspace_id.slice(0, 12)}…</span>}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-3 text-[11px]">
                        <span><strong className="text-ink">{u.counts.linked_candidates}</strong> <span className="text-[#525252]">linked candidates</span></span>
                        <span><strong className={hasOrphans ? "text-[#B91C1C]" : "text-ink"}>{u.counts.email_orphans}</strong> <span className="text-[#525252]">email orphans</span></span>
                        <span><strong className="text-ink">{u.counts.studio_sessions}</strong> <span className="text-[#525252]">studio sessions</span></span>
                    </div>
                </div>

                <div className="flex flex-col gap-1.5 shrink-0">
                    <button
                        type="button"
                        onClick={() => onImpersonate && onImpersonate(u)}
                        data-testid={`user-login-as-btn-${u.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-black uppercase tracking-wider bg-ink text-white hover:opacity-90"
                        title={`Impersonate ${u.email || u.id}`}
                    >
                        <UserSwitch size={11} weight="bold" />
                        Login as
                    </button>
                    {hasOrphans && (
                        <button
                            type="button"
                            onClick={relink}
                            disabled={busy}
                            data-testid={`user-relink-btn-${u.id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-black uppercase tracking-wider bg-[#0EA5E9] text-white hover:opacity-90 disabled:opacity-50"
                        >
                            <Link size={11} weight="bold" />
                            {busy ? "Linking…" : `Relink ${u.counts.email_orphans} orphan${u.counts.email_orphans === 1 ? "" : "s"}`}
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => setExpanded((v) => !v)}
                        data-testid={`user-expand-${u.id}`}
                        className="text-[10px] uppercase tracking-wider font-bold text-[#525252] hover:text-ink"
                    >
                        {expanded ? "Hide details" : "Show details"}
                    </button>
                </div>
            </div>

            {hasProblems && (
                <ul className="mt-2 text-[11px] text-[#92400E] space-y-0.5">
                    {u.problems.map((p, i) => <li key={i}>• {p}</li>)}
                </ul>
            )}

            {expanded && (
                <div className="mt-3 pt-3 border-t border-[#F5F5F5] space-y-3">
                    <DetailBlock title={`Linked candidates (${u.candidates.length})`} tone="cobalt">
                        {u.candidates.length ? (
                            <MiniCandidateList rows={u.candidates} />
                        ) : <Empty />}
                    </DetailBlock>
                    <DetailBlock title={`Email orphans (${u.email_orphans.length})`} tone={hasOrphans ? "warn" : "ink"}>
                        {hasOrphans ? (
                            <MiniCandidateList rows={u.email_orphans} />
                        ) : <Empty />}
                    </DetailBlock>
                    <DetailBlock title={`Resume Studio sessions (${u.studio_sessions.length})`} tone="ink">
                        {u.studio_sessions.length ? (
                            <ul className="space-y-1 text-[11px]">
                                {u.studio_sessions.map((s) => (
                                    <li key={s.id} className="flex gap-3">
                                        <span className="font-mono text-[10px] text-[#A3A3A3]">{(s.id || "").slice(0, 8)}…</span>
                                        <span className="font-bold text-ink">{s.tier || "?"}</span>
                                        <span className="text-[#525252]">{s.status || s.paid ? "paid" : ""}</span>
                                        <span className="text-[#A3A3A3] text-[10px] ml-auto">{s.created_at?.slice(0, 10)}</span>
                                    </li>
                                ))}
                            </ul>
                        ) : <Empty />}
                    </DetailBlock>
                </div>
            )}
        </div>
    );
}


function DetailBlock({ title, tone, children }) {
    const toneCls = {
        cobalt: "text-cobalt",
        warn: "text-[#B91C1C]",
        ink: "text-[#525252]",
    }[tone] || "text-[#525252]";
    return (
        <div>
            <p className={`text-[10px] font-black uppercase tracking-[0.2em] mb-1 ${toneCls}`}>
                {title}
            </p>
            {children}
        </div>
    );
}
function MiniCandidateList({ rows }) {
    return (
        <ul className="space-y-1 text-[11px]">
            {rows.map((c) => (
                <li key={c.id} className="flex gap-3 items-center">
                    <span className="font-mono text-[10px] text-[#A3A3A3]">{c.id.slice(0, 8)}…</span>
                    <span className="font-bold text-ink">{c.stage || "—"}</span>
                    {c.job_id ? (
                        <span className="text-[#525252] text-[10px]">job {c.job_id.slice(0, 8)}…</span>
                    ) : (
                        <span className="text-[#A3A3A3] text-[10px]">no job</span>
                    )}
                    {c.is_team_pool && <span className="text-[9px] uppercase font-bold text-emerald-700">team pool</span>}
                    {c.is_archived && <span className="text-[9px] uppercase font-bold text-red-600">archived</span>}
                    <span className="text-[#A3A3A3] text-[10px] ml-auto">{c.created_at?.slice(0, 10)}</span>
                </li>
            ))}
        </ul>
    );
}
function Empty() {
    return <p className="text-[11px] text-[#A3A3A3]">— none.</p>;
}
